import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import { Form, Input, Drawer } from "antd"
import { useScreenBreakpoints } from "hooks/hook"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Button } from "components"
import { ewaAxiosInstance } from "utils/request"
import {
  calculateOvertimeDuration,
  convertTo12Hr,
  convertTo24Hr,
  validateTimeInput,
} from "../utils"
import { useEffect } from "react"
import { useAppNotificationStore } from "stores"
import { onQueryError } from "utils/utils"
import { TimeInput } from "../components/TimeInput"

interface Props {
  open: boolean
  onClose: () => void
  queryKey?: string
  mode?: "new" | "addReason"
  prefill?: {
    date: string
    overtimeStart: string
    overtimeEnd: string
    id: number | null
    reason?: string
  }
}

const OvertimeEntryModal = ({
  open,
  onClose,
  queryKey,
  mode,
  prefill,
}: Props) => {
  const [form] = Form.useForm()
  const isMobile = useScreenBreakpoints("sm")
  const queryClient = useQueryClient()
  const { toast } = useAppNotificationStore()
  const Start = Form.useWatch("overtimeStart", form)
  const End = Form.useWatch("overtimeEnd", form)

  const isAddReason = mode === "addReason"

  const { mutate: submitEntry, isLoading } = useMutation({
    mutationFn: async (body: {
      date: string
      overtimeStartTime: string
      overtimeEndTime: string
      reason: string
    }) => {
      return (await ewaAxiosInstance.post(`/v1/time-attendance/overtime`, body))
        .data
    },
    onSuccess: (data: any) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: [queryKey] })
        form.resetFields()
        onClose()
        data.message && toast.success(data.message)
      }
    },
    onError: onQueryError,
  })

  const { mutate: addReason, isLoading: isLoadingReason } = useMutation({
    mutationFn: async (body: { reason: string }) => {
      return (
        await ewaAxiosInstance.post(
          `/v1/time-attendance/employee/overtime/${prefill?.id}/add-reason`,
          body
        )
      ).data
    },
    onSuccess: (data: any) => {
      if (data.status === "success") {
        queryClient.invalidateQueries({ queryKey: [queryKey] })
        form.resetFields()
        onClose()
      } else {
        toast.error(data.msg)
      }
    },
    onError: onQueryError,
  })

  useEffect(() => {
    if (open && prefill) {
      const formattedDate = prefill.date.split("T")[0]
      const [yyyy, mm, dd] = formattedDate.split("-")
      form.setFieldsValue({
        dd,
        mm,
        yyyy,
        overtimeStart: convertTo12Hr(prefill.overtimeStart),
        overtimeEnd: convertTo12Hr(prefill.overtimeEnd),
        reason: prefill.reason || "",
      })
    }
    if (!open) {
      form.resetFields()
    }
  }, [open, prefill, isAddReason])

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (isAddReason) {
          addReason({ reason: values.reason })
        } else {
          submitEntry({
            date: `${values.yyyy}-${values.mm}-${values.dd}`,
            overtimeStartTime: convertTo24Hr(values.overtimeStart),
            overtimeEndTime: convertTo24Hr(values.overtimeEnd),
            reason: values.reason,
          })
        }
      })
      .catch(() => {})
  }

  const overtimeDuration = () => {
    if (!Start || !End) return ""
    return calculateOvertimeDuration(convertTo24Hr(Start), convertTo24Hr(End))
  }

  const formContent = (
    <Form
      form={form}
      layout="vertical"
      className="flex flex-col gap-1"
      style={{ marginBottom: 0 }}
    >
      <Form.Item label="Day">
        <div className="flex items-center gap-2">
          <Form.Item
            name="dd"
            noStyle
            rules={[{ required: true, message: "Please enter day" }]}
          >
            <Input
              placeholder="DD"
              className="text-center"
              disabled={isAddReason}
            />
          </Form.Item>
          <span className="text-gray-400">/</span>
          <Form.Item
            name="mm"
            noStyle
            rules={[{ required: true, message: "Please enter month" }]}
          >
            <Input
              placeholder="MM"
              className="text-center"
              disabled={isAddReason}
            />
          </Form.Item>
          <span className="text-gray-400">/</span>
          <Form.Item
            name="yyyy"
            noStyle
            rules={[{ required: true, message: "Please enter year" }]}
          >
            <Input
              placeholder="YYYY"
              className="text-center"
              disabled={isAddReason}
            />
          </Form.Item>
        </div>
      </Form.Item>

      <div className="grid grid-cols-2 gap-4">
        <Form.Item
          name="overtimeStart"
          label="Overtime start"
          rules={[
            { required: true, message: "Please enter overtime start time" },
            {
              validator: async (_, val) => {
                const err = validateTimeInput(val)
                if (err) throw new Error(err)
              },
            },
          ]}
        >
          <TimeInput
            defaultValue="PM"
            disabled={isAddReason}
          />
        </Form.Item>

        <Form.Item
          name="overtimeEnd"
          label="Overtime end"
          rules={[
            { required: true, message: "Please enter overtime end time" },
            {
              validator: async (_, val) => {
                const err = validateTimeInput(val)
                if (err) throw new Error(err)
              },
            },
          ]}
        >
          <TimeInput
            defaultValue="PM"
            disabled={isAddReason}
          />
        </Form.Item>
      </div>

      <div className="flex items-center gap-2 mb-2 -mt-2">
        <span className="text-sm text-slate-600">Overtime duration:</span>
        <span className="text-sm font-semibold text-slate-800">
          {overtimeDuration()}
        </span>
      </div>

      <hr />

      <Form.Item
        name="reason"
        label="Reason"
        style={{ marginBottom: 0 }}
        rules={[{ required: true, message: "Please enter a reason" }]}
      >
        <Input.TextArea
          rows={4}
          maxLength={150}
          placeholder="Enter reasons for your overtime"
          className="text-sm resize-none"
        />
      </Form.Item>

      <p className="text-xs text-slate-400 mt-1">
        Note: You can't edit your reason after saving
      </p>
    </Form>
  )

  const saveButton = (
    <Button
      type="button"
      onClick={handleSubmit}
      submitting={isLoading || isLoadingReason}
      disabled={isLoading || isLoadingReason}
      className="w-full bg-primary500 text-white py-2.5 rounded-md text-sm font-semibold mt-2 hover:opacity-90 transition-opacity disabled:opacity-50"
    >
      Save entry
    </Button>
  )

  return (
    <>
      <CustomModal
        open={open && isMobile}
        onCancel={onClose}
        title="Overtime entry"
        width={444}
        className="[&_.ant-modal-content]:h-fit [&_.ant-modal-body]:!mt-2"
        footer={saveButton}
      >
        {formContent}
      </CustomModal>

      <Drawer
        title="Overtime entry"
        placement="bottom"
        width={412}
        height={500}
        onClose={onClose}
        open={open && !isMobile}
      >
        {formContent}
        {saveButton}
      </Drawer>
    </>
  )
}

export default OvertimeEntryModal
